// Rivalries: each school's rival and the rivalry's name, from the coach's rivalry sheet (October 2026), matched
// to the schools in the state worlds (src/data/*.json). A school lists one rival; the rival may list someone else
// (one-way) or the same school under its own name for the game. Rivals the sheet names that aren't in the game
// (smaller classes, private schools in other leagues) are left out, so some schools have no rival. Easton Area
// and Phillipsburg are each other's rival across the Pennsylvania / New Jersey line.

/** A school's rival: the rival's name in its state's world data and what the school calls the game. */
export interface Rivalry {
  rival: string;
  name: string;
  rivalState?: string; // when the rival plays in another state
}

/** State -> school -> its rival. */
export const RIVALRIES: Record<string, Record<string, Rivalry>> = {
  Alabama: {
    'Auburn High': { rival: 'Opelika', name: '91-Year Rivalry' },
    Baker: { rival: 'Mary G. Montgomery', name: 'West Mobile Rivalry' },
    'Bob Jones': { rival: 'James Clemens', name: 'Madison City Rivalry' },
    Buckhorn: { rival: 'Hazel Green', name: 'Madison County Rivalry' },
    'Central-Phenix City': { rival: 'Smiths Station', name: 'Phenix City Area Rivalry' },
    Daphne: { rival: 'Fairhope', name: 'Jubilee Cup' },
    Dothan: { rival: 'Enterprise', name: 'Wiregrass Rivalry' },
    Enterprise: { rival: 'Dothan', name: 'Wiregrass Rivalry' },
    Fairhope: { rival: 'Daphne', name: 'Jubilee Cup' },
    Foley: { rival: 'Robertsdale', name: 'Baldwin County Rivalry' },
    Grissom: { rival: 'Huntsville High', name: 'Huntsville City Rivalry' },
    'Hazel Green': { rival: 'Buckhorn', name: 'Madison County Rivalry' },
    Hoover: { rival: 'Spain Park', name: 'Battle of Hoover' },
    'Huntsville High': { rival: 'Grissom', name: 'Huntsville City Rivalry' },
    'James Clemens': { rival: 'Bob Jones', name: 'Madison City Rivalry' },
    'Mary G. Montgomery': { rival: 'Baker', name: 'West Mobile Rivalry' },
    'Oak Mountain': { rival: 'Spain Park', name: 'US 280 Rivalry' },
    Opelika: { rival: 'Auburn High', name: 'Lee County Rivalry' },
    Robertsdale: { rival: 'Foley', name: 'Baldwin County Rivalry' },
    'Smiths Station': { rival: 'Central-Phenix City', name: 'Lee/Russell County Rivalry' },
    'Spain Park': { rival: 'Hoover', name: 'Battle of Hoover' },
    Sparkman: { rival: 'Bob Jones', name: 'Highway 72 Rivalry' }
  },
  California: {
    Antioch: { rival: 'Pittsburg', name: 'Big Little Game' },
    'Archbishop Mitty': { rival: 'Valley Christian', name: 'WCAL South Bay Rivalry' },
    'Archbishop Riordan': { rival: 'Sacred Heart Cathedral', name: 'Stanfel Cup' },
    'Bellarmine College Prep': { rival: 'Saint Francis', name: 'Holy War' },
    Buchanan: { rival: 'Clovis North', name: 'Education Center Rivalry' },
    'California High': { rival: 'Amador Valley', name: 'EBAL Rivalry' },
    Carlsbad: { rival: 'La Costa Canyon', name: 'Avocado League Rivalry' },
    Central: { rival: 'Clovis West', name: 'TRAC Rivalry' },
    Chaminade: { rival: 'Sherman Oaks Notre Dame', name: 'Mission League Rivalry' },
    'Clayton Valley Charter': { rival: 'De La Salle', name: 'Concord Rivalry' },
    'Clovis East': { rival: 'Clovis High', name: 'TRAC Rivalry' },
    'Clovis High': { rival: 'Clovis West', name: 'Battle of the Cougars and Eagles' },
    'Clovis North': { rival: 'Buchanan', name: 'TRAC Rivalry' },
    'Clovis West': { rival: 'Buchanan', name: 'Highway 41 Rivalry' },
    Compton: { rival: 'Long Beach Jordan', name: 'Moore League Rivalry' },
    'Corona Centennial': { rival: 'Norco', name: 'Big VIII League Rivalry' },
    'Corona del Mar': { rival: 'Newport Harbor', name: 'Battle of the Bay' },
    Damien: { rival: 'Chino Hills', name: 'Baseline League Rivalry' },
    'De La Salle': { rival: 'Clayton Valley Charter', name: 'Concord Rivalry' },
    'Del Oro': { rival: 'Granite Bay', name: 'Sierra Foothill League Rivalry' },
    'Eastvale Roosevelt': { rival: 'Corona Centennial', name: 'Big VIII League Rivalry' },
    'Encino Crespi': { rival: 'Sherman Oaks Notre Dame', name: 'Battle of the Boulevard' },
    Folsom: { rival: 'Oak Ridge', name: 'Sierra Foothill League Rivalry' },
    'Fountain Valley': { rival: 'Huntington Beach Edison', name: 'Battle for the Bell' },
    'Gardena Serra': { rival: 'Chaminade', name: 'Mission League Rivalry' },
    'Granite Bay': { rival: 'Del Oro', name: 'Sierra Foothill League Rivalry' },
    'Granite Hills': { rival: 'Helix Charter', name: 'Grossmont Hills Rivalry' },
    'Helix Charter': { rival: 'Mount Miguel', name: 'Battle for the Highlander Sword' },
    'Huntington Beach Edison': { rival: 'Fountain Valley', name: 'Battle for the Bell' },
    'JSerra Catholic': { rival: 'Santa Margarita', name: 'Trinity League Rivalry' },
    'La Costa Canyon': { rival: 'Torrey Pines', name: 'Beach Bowl' },
    Lakewood: { rival: 'Long Beach Millikan', name: 'Moore League Rivalry' },
    Lincoln: { rival: 'Cathedral Catholic', name: 'Western League Rivalry' },
    'Long Beach Jordan': { rival: 'Compton', name: 'Moore League Rivalry' },
    'Long Beach Millikan': { rival: 'Lakewood', name: 'Moore League Rivalry' },
    'Los Alamitos': { rival: 'Huntington Beach Edison', name: 'Battle for the Bell' },
    Loyola: { rival: 'Sherman Oaks Notre Dame', name: 'Battle for the Shield' },
    'Mater Dei': { rival: 'Servite', name: 'Trinity League / Bell Game' },
    'Mission Hills': { rival: 'San Marcos', name: 'Discovery Bowl' },
    'Mission Viejo': { rival: 'San Clemente', name: 'South Coast League Rivalry' },
    'Monte Vista': { rival: 'San Ramon Valley', name: 'Mayor\'s Cup' },
    'Monterey Trail': { rival: 'Elk Grove', name: 'Delta League Rivalry' },
    'Mount Miguel': { rival: 'Helix Charter', name: 'Grossmont League Rivalry' },
    'Murrieta Valley': { rival: 'Vista Murrieta', name: 'Southwestern League Rivalry' },
    'Newport Harbor': { rival: 'Corona del Mar', name: 'Battle of the Bay' },
    Norco: { rival: 'Corona Centennial', name: 'Big VIII League Rivalry' },
    'Oak Ridge': { rival: 'Folsom', name: 'Highway 50 Rivalry' },
    'Orange Lutheran': { rival: 'Servite', name: 'Trinity League Rivalry' },
    Pittsburg: { rival: 'Antioch', name: 'Big Little Game' },
    'Rancho Cucamonga': { rival: 'Upland', name: 'Baseline League Rivalry' },
    Rocklin: { rival: 'Whitney', name: 'Quarry Bowl' },
    'Sacred Heart Cathedral': { rival: 'St. Ignatius', name: 'Bruce-Mahoney Trophy' },
    'Saint Francis': { rival: 'Bellarmine College Prep', name: 'Holy War' },
    'San Clemente': { rival: 'Mission Viejo', name: 'South Coast League Rivalry' },
    'San Marcos': { rival: 'Mission Hills', name: 'Discovery Bowl' },
    'San Mateo Serra (Junipero Serra)': { rival: 'Saint Francis', name: 'WCAL Rivalry' },
    'San Ramon Valley': { rival: 'Monte Vista', name: 'Mayor\'s Cup' },
    'Santa Margarita': { rival: 'JSerra Catholic', name: 'Trinity League Rivalry' },
    Servite: { rival: 'Mater Dei', name: 'Trinity League / Bell Game' },
    'Sherman Oaks Notre Dame': { rival: 'Encino Crespi', name: 'Battle of the Boulevard' },
    'Sierra Canyon': { rival: 'Oaks Christian', name: 'Mission League Rivalry' },
    'St. Bonaventure': { rival: 'Oaks Christian', name: 'Marmonte League Rivalry' },
    'St. Ignatius': { rival: 'Sacred Heart Cathedral', name: 'Bruce-Mahoney Trophy' },
    'St. John Bosco': { rival: 'Mater Dei', name: 'Trinity League Rivalry' },
    'Torrey Pines': { rival: 'La Costa Canyon', name: 'Beach Bowl' },
    Upland: { rival: 'Rancho Cucamonga', name: 'Baseline League Rivalry' },
    'Valley Christian': { rival: 'Archbishop Mitty', name: 'WCAL South Bay Rivalry' },
    'Vista Murrieta': { rival: 'Murrieta Valley', name: 'Southwestern League Rivalry' },
    Whitney: { rival: 'Rocklin', name: 'Quarry Bowl' }
  },
  Florida: {
    'American Heritage': { rival: 'Cardinal Gibbons', name: 'Broward Private School Rivalry' },
    Apopka: { rival: 'West Orange', name: 'Old Metro Conference Rivalry' },
    'Atlantic Coast': { rival: 'Mandarin', name: 'Southside Jacksonville Rivalry' },
    'Boca Raton': { rival: 'Spanish River', name: 'Boca Bowl' },
    Boone: { rival: 'Edgewater', name: 'Battle for the Barrel' },
    'Cardinal Gibbons': { rival: 'St. Thomas Aquinas', name: 'Fort Lauderdale Catholic Rivalry' },
    Celebration: { rival: 'Poinciana', name: 'Osceola County Rivalry' },
    Centennial: { rival: 'Treasure Coast', name: 'Port St. Lucie Rivalry' },
    'Chaminade-Madonna': { rival: 'American Heritage', name: 'South Florida Private Rivalry' },
    'Coral Gables': { rival: 'Miami High', name: 'Oldest Miami Rivalry' },
    'Cypress Bay': { rival: 'Western', name: 'West Broward Rivalry' },
    Davenport: { rival: 'Ridge Community', name: 'Davenport Rivalry' },
    DeLand: { rival: 'Mainland', name: 'Oldest Volusia Rivalry' },
    'Dr. Joaquin Garcia': { rival: 'Lake Worth', name: 'Lake Worth Area Rivalry' },
    'Dr. Phillips': { rival: 'West Orange', name: 'Southwest Orlando Rivalry' },
    Durant: { rival: 'Newsome', name: 'Alafia River Rivalry' },
    Edgewater: { rival: 'Boone', name: 'Battle for the Barrel' },
    Gateway: { rival: 'St. Cloud', name: 'Osceola County Rivalry' },
    'George Jenkins': { rival: 'Lakeland', name: 'Lakeland City Rivalry' },
    'Haines City': { rival: 'Ridge Community', name: 'East Polk Rivalry' },
    Harmony: { rival: 'St. Cloud', name: 'Soldier City Classic' },
    Homestead: { rival: 'South Dade', name: 'Harris Field Classic' },
    'Lake Mary': { rival: 'Seminole', name: 'Battle of Rinehart Road' },
    'Lake Worth': { rival: 'Santaluces', name: 'Central Palm Beach Rivalry' },
    Mandarin: { rival: 'Sandalwood', name: 'Battle of the Southside' },
    'Miami Carol City': { rival: 'Miami Northwestern', name: 'Legends Bowl' },
    'Miami Central': { rival: 'Miami Northwestern', name: '305 Classic' },
    'Miami High': { rival: 'Coral Gables', name: 'Oldest Miami Rivalry' },
    'Miami Northwestern': { rival: 'Miami Central', name: '305 Classic' },
    Newsome: { rival: 'Durant', name: 'Alafia River Rivalry' },
    Nova: { rival: 'South Plantation', name: 'Central Broward Rivalry' },
    Ocoee: { rival: 'West Orange', name: 'West Orange County Rivalry' },
    Olympia: { rival: 'Dr. Phillips', name: 'Southwest Orlando Rivalry' },
    'Palm Beach Central': { rival: 'Wellington', name: 'Wellington Cup' },
    'Park Vista': { rival: 'Santaluces', name: 'Hypoluxo Road Rivalry' },
    'Plant City': { rival: 'Durant', name: 'Battle for the Redman Cup' },
    'Ridge Community': { rival: 'Haines City', name: 'Battle of Highway 27' },
    Sandalwood: { rival: 'Mandarin', name: 'Battle of the Southside' },
    Santaluces: { rival: 'Park Vista', name: 'Hypoluxo Road Rivalry' },
    Sarasota: { rival: 'Sarasota Riverview', name: 'City of Sarasota Rivalry' },
    'Sarasota Riverview': { rival: 'Venice', name: 'South Sarasota County Rivalry' },
    Seminole: { rival: 'Lake Mary', name: 'Battle of Rinehart Road' },
    'South Dade': { rival: 'Homestead', name: 'Harris Field Classic' },
    'Spanish River': { rival: 'Boca Raton', name: 'Boca Bowl' },
    'St. Cloud': { rival: 'Harmony', name: 'Soldier City Classic' },
    'St. Thomas Aquinas': { rival: 'Chaminade-Madonna', name: 'South Florida Powerhouse Rivalry' },
    'Strawberry Crest': { rival: 'Plant City', name: 'East Hillsborough Rivalry' },
    'Treasure Coast': { rival: 'Centennial', name: 'Port St. Lucie Rivalry' },
    University: { rival: 'DeLand', name: 'West Volusia Rivalry' },
    Venice: { rival: 'Sarasota Riverview', name: 'South Sarasota County Rivalry' },
    'Vero Beach': { rival: 'Fort Pierce Central', name: 'Treasure Coast Rivalry' },
    Wellington: { rival: 'Palm Beach Central', name: 'Wellington Cup' },
    'West Orange': { rival: 'Apopka', name: 'Metro West Rivalry' },
    Western: { rival: 'Cypress Bay', name: 'West Broward Rivalry' },
    'Winter Park': { rival: 'Boone', name: 'Orange County Rivalry' }
  },
  Georgia: {
    Archer: { rival: 'Grayson', name: 'Eastern Gwinnett Rivalry' },
    Brookwood: { rival: 'Parkview', name: 'Battle of Five Forks Trickum' },
    Buford: { rival: 'Mill Creek', name: 'Northern Gwinnett Rivalry' },
    Campbell: { rival: 'McEachern', name: 'South Cobb Rivalry' },
    'Central Gwinnett': { rival: 'Discovery', name: 'Lawrenceville Rivalry' },
    'Collins Hill': { rival: 'North Gwinnett', name: 'Suwanee Rivalry' },
    'Colquitt County': { rival: 'Valdosta', name: 'Region 1-6A Rivalry' },
    Dacula: { rival: 'Mill Creek', name: 'Hamilton Mill Rivalry' },
    Denmark: { rival: 'West Forsyth', name: 'South Forsyth County Rivalry' },
    Discovery: { rival: 'Central Gwinnett', name: 'Lawrenceville Rivalry' },
    Duluth: { rival: 'Norcross', name: 'Old Peachtree Road Rivalry' },
    'Forsyth Central': { rival: 'North Forsyth', name: 'Original Forsyth Rivalry' },
    Grayson: { rival: 'Brookwood', name: 'Snellville/Loganville Rivalry' },
    Harrison: { rival: 'McEachern', name: 'West Cobb Rivalry' },
    Hillgrove: { rival: 'McEachern', name: 'Powder Springs Rivalry' },
    'Kennesaw Mountain': { rival: 'North Cobb', name: 'Kennesaw Rivalry' },
    Lambert: { rival: 'South Forsyth', name: 'Battle of Old Atlanta Road' },
    Lowndes: { rival: 'Valdosta', name: 'Winnersville Classic' },
    Marietta: { rival: 'North Cobb', name: 'Cobb County Rivalry' },
    McEachern: { rival: 'Hillgrove', name: 'Powder Springs Rivalry' },
    'Mill Creek': { rival: 'Buford', name: 'Northern Gwinnett Rivalry' },
    'North Cobb': { rival: 'Harrison', name: 'Kennesaw Rivalry' },
    'North Forsyth': { rival: 'Forsyth Central', name: 'Coal Mountain Rivalry' },
    'North Gwinnett': { rival: 'Collins Hill', name: 'Suwanee Rivalry' },
    Parkview: { rival: 'Brookwood', name: 'Battle of Five Forks Trickum' },
    'Peachtree Ridge': { rival: 'Collins Hill', name: 'Old Peachtree Rivalry' },
    'South Forsyth': { rival: 'Lambert', name: 'Battle of Old Atlanta Road' },
    'South Gwinnett': { rival: 'Brookwood', name: 'Battle of Snellville' },
    Valdosta: { rival: 'Lowndes', name: 'Winnersville Classic' },
    Walton: { rival: 'Wheeler', name: 'East Cobb Rivalry' },
    'West Forsyth': { rival: 'South Forsyth', name: 'West/South Forsyth Rivalry' },
    Wheeler: { rival: 'Walton', name: 'East Cobb Rivalry' }
  },
  Louisiana: {
    Acadiana: { rival: 'Carencro', name: 'Lafayette Parish Rivalry' },
    Airline: { rival: 'Parkway', name: 'Bossier City Rivalry' },
    'Alexandria Senior High (ASH)': { rival: 'Pineville', name: 'Red River Rivalry' },
    Barbe: { rival: 'Sulphur', name: 'Lake Charles/Sulphur Rivalry' },
    Benton: { rival: 'Haughton', name: 'Bossier Parish Rivalry' },
    Bonnabel: { rival: 'East Jefferson', name: 'Metairie Rivalry' },
    'Brother Martin': { rival: 'Jesuit', name: 'Gentilly/Mid-City Catholic Rivalry' },
    'C.E. Byrd': { rival: 'Captain Shreve', name: 'The Backyard Brawl' },
    'Captain Shreve': { rival: 'C.E. Byrd', name: 'The Backyard Brawl' },
    Carencro: { rival: 'Acadiana', name: 'Lafayette Parish Rivalry' },
    'Catholic-Baton Rouge': { rival: 'Zachary', name: 'District 4-5A Rivalry' },
    'Central-Baton Rouge': { rival: 'Zachary', name: 'North East Baton Rouge Rivalry' },
    Chalmette: { rival: 'Holy Cross', name: 'Lower 9th/St. Bernard Rivalry' },
    Covington: { rival: 'Mandeville', name: 'West St. Tammany Rivalry' },
    'Denham Springs': { rival: 'Live Oak', name: 'Livingston Parish Rivalry' },
    Destrehan: { rival: 'Hahnville', name: 'Battle on the River' },
    Dutchtown: { rival: 'St. Amant', name: 'Ascension Parish Rivalry' },
    'East Ascension': { rival: 'St. Amant', name: 'Ascension Parish Rivalry' },
    'East Jefferson': { rival: 'Bonnabel', name: 'Metairie Rivalry' },
    'Edna Karr': { rival: 'Warren Easton', name: 'New Orleans Public Rivalry' },
    Fontainebleau: { rival: 'Mandeville', name: 'Mandeville City Rivalry' },
    'H.L. Bourgeois': { rival: 'Terrebonne', name: 'Houma Rivalry' },
    Hahnville: { rival: 'Destrehan', name: 'Battle on the River' },
    Hammond: { rival: 'Ponchatoula', name: 'Tangipahoa Parish Rivalry' },
    Haughton: { rival: 'Benton', name: 'Bossier Parish Rivalry' },
    'Holy Cross': { rival: 'Jesuit', name: 'Great American Rivalry' },
    Jesuit: { rival: 'Holy Cross', name: 'Great American Rivalry' },
    'John Curtis Christian': { rival: 'Archbishop Rummel', name: 'Jefferson Parish Rivalry' },
    'John Ehret': { rival: 'West Jefferson', name: 'Westbank Rivalry' },
    'L.W. Higgins': { rival: 'West Jefferson', name: 'Westbank Rivalry' },
    'Liberty Magnet': { rival: 'Woodlawn-Baton Rouge', name: 'Baton Rouge Rivalry' },
    'Live Oak': { rival: 'Denham Springs', name: 'Livingston Parish Rivalry' },
    Mandeville: { rival: 'Fontainebleau', name: 'Mandeville City Rivalry' },
    'Natchitoches Central': { rival: 'Pineville', name: 'Central Louisiana Rivalry' },
    Neville: { rival: 'Ouachita Parish', name: 'Monroe Rivalry' },
    Northshore: { rival: 'Slidell', name: 'East St. Tammany Rivalry' },
    'Ouachita Parish': { rival: 'Neville', name: 'Monroe Rivalry' },
    Parkway: { rival: 'Airline', name: 'Bossier City Rivalry' },
    Pineville: { rival: 'Alexandria Senior High (ASH)', name: 'Red River Rivalry' },
    Ponchatoula: { rival: 'Hammond', name: 'Tangipahoa Parish Rivalry' },
    Prairieville: { rival: 'Dutchtown', name: 'Ascension Parish Rivalry' },
    Riverdale: { rival: 'East Jefferson', name: 'Jefferson Highway Rivalry' },
    Ruston: { rival: 'West Monroe', name: 'I-20 Rivalry' },
    Salmen: { rival: 'Slidell', name: 'Slidell City Rivalry' },
    'Sam Houston': { rival: 'Sulphur', name: 'Calcasieu Parish Rivalry' },
    Slidell: { rival: 'Salmen', name: 'Slidell City Rivalry' },
    'St. Amant': { rival: 'East Ascension', name: 'The Ascension Parish Pit' },
    'St. Augustine': { rival: 'Jesuit', name: 'Catholic League Rivalry' },
    'St. Paul\'s': { rival: 'Covington', name: 'Bogie Falaya River Rivalry' },
    Sulphur: { rival: 'Barbe', name: 'Lake Charles/Sulphur Rivalry' },
    Terrebonne: { rival: 'Thibodaux', name: 'Bayou Lafourche Rivalry' },
    Thibodaux: { rival: 'Terrebonne', name: 'Bayou Lafourche Rivalry' },
    Walker: { rival: 'Denham Springs', name: 'Livingston Parish Rivalry' },
    'Warren Easton': { rival: 'Edna Karr', name: 'New Orleans Public Rivalry' },
    'West Jefferson': { rival: 'L.W. Higgins', name: 'Westbank Rivalry' },
    'West Monroe': { rival: 'Ruston', name: 'I-20 Rivalry' },
    Zachary: { rival: 'Central-Baton Rouge', name: 'North East Baton Rouge Rivalry' }
  },
  Maryland: {
    Annapolis: { rival: 'Broadneck', name: 'Annapolis Cup' },
    'Bethesda-Chevy Chase': { rival: 'Walter Johnson', name: 'Battle of Bethesda' },
    Bladensburg: { rival: 'Parkdale', name: 'Peace Cross Rivalry' },
    Bowie: { rival: 'Eleanor Roosevelt', name: 'Route 197 Rivalry' },
    'Charles H. Flowers': { rival: 'Dr. Henry A. Wise', name: 'PG 4A Rivalry' },
    'Dr. Henry A. Wise': { rival: 'Charles H. Flowers', name: 'PG 4A Rivalry' },
    DuVal: { rival: 'Eleanor Roosevelt', name: 'Lanham/Greenbelt Rivalry' },
    'Eleanor Roosevelt': { rival: 'Bowie', name: 'Route 197 Rivalry' },
    Gaithersburg: { rival: 'Quince Orchard', name: 'Gaithersburg City Rivalry' },
    'Glen Burnie': { rival: 'North County', name: 'Glen Burnie Rivalry' },
    'High Point': { rival: 'Northwestern', name: 'North PG County Rivalry' },
    'James Hubert Blake': { rival: 'Paint Branch', name: 'Northeast Consortium Rivalry' },
    Laurel: { rival: 'Bowie', name: 'North PG Rivalry' },
    'North County': { rival: 'Glen Burnie', name: 'Glen Burnie Rivalry' },
    Northwest: { rival: 'Seneca Valley', name: 'King of Germantown' },
    Northwestern: { rival: 'Bladensburg', name: 'Route 1 Rivalry' },
    Parkdale: { rival: 'Bladensburg', name: 'Peace Cross Rivalry' },
    'Quince Orchard': { rival: 'Northwest', name: 'Cougar-Jaguar Rivalry' },
    'Seneca Valley': { rival: 'Northwest', name: 'King of Germantown' },
    'Walter Johnson': { rival: 'Bethesda-Chevy Chase', name: 'Battle of Bethesda' }
  },
  'New Jersey': {
    Barringer: { rival: 'East Orange Campus', name: 'Oldest NJ Thanksgiving Rivalry' },
    'Bergen Catholic': { rival: 'Don Bosco Prep', name: 'Bergen County Catholic Rivalry' },
    Bloomfield: { rival: 'Montclair', name: 'Essex County Rivalry' },
    'Bridgewater-Raritan': { rival: 'Hillsborough', name: 'Somerset County Rivalry' },
    Clifton: { rival: 'Passaic High', name: 'Thanksgiving Rivalry' },
    Delbarton: { rival: 'Seton Hall Prep', name: 'North Jersey Non-Public Rivalry' },
    'Don Bosco Prep': { rival: 'Bergen Catholic', name: 'Bergen County Catholic Rivalry' },
    'Donovan Catholic': { rival: 'Toms River North', name: 'Toms River Rivalry' },
    'East Brunswick': { rival: 'Old Bridge', name: 'Thanksgiving Rivalry' },
    'East Orange Campus': { rival: 'Barringer', name: 'Oldest NJ Thanksgiving Rivalry' },
    Eastern: { rival: 'Cherokee', name: 'Voorhees/Marlton Rivalry' },
    Elizabeth: { rival: 'Linden', name: 'Union County Rivalry' },
    Hillsborough: { rival: 'Bridgewater-Raritan', name: 'Route 206 Rivalry' },
    Lenape: { rival: 'Cherokee', name: 'Lenape District Rivalry' },
    Linden: { rival: 'Union High', name: 'Thanksgiving Rivalry' },
    Montclair: { rival: 'Bloomfield', name: 'Essex County Rivalry' },
    'Old Bridge': { rival: 'East Brunswick', name: 'Thanksgiving Rivalry' },
    'Passaic County Tech': { rival: 'Clifton', name: 'Passaic County Rivalry' },
    'Passaic High': { rival: 'Clifton', name: 'Thanksgiving Rivalry' },
    Phillipsburg: { rival: 'Easton Area', name: 'Thanksgiving Day Game', rivalState: 'Pennsylvania' },
    Piscataway: { rival: 'Sayreville', name: 'GMC Red Division Rivalry' },
    Plainfield: { rival: 'Westfield', name: 'Thanksgiving Day Rivalry' },
    'Pope John XXIII': { rival: 'Delbarton', name: 'Northwest Jersey Catholic Rivalry' },
    Sayreville: { rival: 'Old Bridge', name: 'Route 9 Rivalry' },
    'Seton Hall Prep': { rival: 'Delbarton', name: 'Super Football Conference Rivalry' },
    'Southern Regional': { rival: 'Toms River North', name: 'Shore Conference Rivalry' },
    'St. Joseph Regional': { rival: 'Bergen Catholic', name: 'Big North United Rivalry' },
    'St. Peter\'s Prep': { rival: 'Seton Hall Prep', name: 'Essex/Hudson Catholic Rivalry' },
    'Union High': { rival: 'Linden', name: 'Thanksgiving Rivalry' },
    'Washington Township': { rival: 'Williamstown', name: 'Gloucester County Rivalry' },
    'West Orange': { rival: 'Montclair', name: 'Super Football Conference Rivalry' },
    Westfield: { rival: 'Plainfield', name: 'Thanksgiving Day Rivalry' },
    Williamstown: { rival: 'Washington Township', name: 'Gloucester County Rivalry' }
  },
  'North Carolina': {
    Apex: { rival: 'Apex Friendship', name: 'Peak City Rivalry' },
    'Apex Friendship': { rival: 'Apex', name: 'Peak City Rivalry' },
    'Ardrey Kell': { rival: 'Providence', name: 'South Charlotte Rivalry' },
    Broughton: { rival: 'Enloe', name: 'Cap 6 / Downtown Raleigh Rivalry' },
    Chambers: { rival: 'Mallard Creek', name: 'North Charlotte Rivalry' },
    Enloe: { rival: 'Broughton', name: 'Downtown Raleigh Rivalry' },
    Garinger: { rival: 'East Mecklenburg', name: 'East Charlotte Rivalry' },
    'Green Hope': { rival: 'Panther Creek', name: 'West Cary Rivalry' },
    'Green Level': { rival: 'Panther Creek', name: 'West Cary Rivalry' },
    Hough: { rival: 'Mallard Creek', name: 'I-77 North Rivalry' },
    'Mallard Creek': { rival: 'Hough', name: 'North Mecklenburg Rivalry' },
    Millbrook: { rival: 'Wakefield', name: 'North Raleigh Rivalry' },
    'Myers Park': { rival: 'South Mecklenburg', name: 'South Charlotte Rivalry' },
    'Panther Creek': { rival: 'Green Hope', name: 'West Cary Rivalry' },
    Providence: { rival: 'Ardrey Kell', name: 'South Charlotte Rivalry' },
    'South Mecklenburg': { rival: 'Myers Park', name: 'South Charlotte Rivalry' }
  },
  Ohio: {
    Brunswick: { rival: 'Strongsville', name: 'Route 42 Rivalry' },
    Centerville: { rival: 'Kettering Fairmont', name: 'Battle of Far Hills' },
    'Cincinnati Archbishop Moeller': { rival: 'Cincinnati St. Xavier', name: 'GCL South Rivalry' },
    'Cincinnati Colerain': { rival: 'Cincinnati Princeton', name: 'GMC Rivalry' },
    'Cincinnati Elder': { rival: 'Cincinnati Oak Hills', name: 'West Side Rivalry' },
    'Cincinnati Oak Hills': { rival: 'Cincinnati Western Hills', name: 'West Side Public Rivalry' },
    'Cincinnati Princeton': { rival: 'Mason', name: 'GMC Rivalry' },
    'Cincinnati St. Xavier': { rival: 'Cincinnati Elder', name: 'GCL South Rivalry' },
    'Cincinnati West Clermont': { rival: 'Milford', name: 'ECC Rivalry' },
    'Cleveland St. Ignatius': { rival: 'Lakewood St. Edward', name: 'The Holy War' },
    'Delaware Olentangy Berlin': { rival: 'Lewis Center Olentangy', name: 'Olentangy District Rivalry' },
    'Dublin Coffman': { rival: 'Upper Arlington', name: 'OCC Central Rivalry' },
    'Dublin Jerome': { rival: 'Dublin Coffman', name: 'Dublin City Rivalry' },
    Fairfield: { rival: 'Hamilton', name: 'Route 4 Rivalry' },
    'Galloway Westland': { rival: 'Grove City', name: 'South-Western City Schools Rivalry' },
    Hamilton: { rival: 'Middletown', name: 'Butler County Rivalry' },
    'Hilliard Bradley': { rival: 'Hilliard Davidson', name: 'Hilliard City Rivalry' },
    'Hilliard Darby': { rival: 'Hilliard Davidson', name: 'Apple Crate Game' },
    'Hilliard Davidson': { rival: 'Hilliard Darby', name: 'Apple Crate Game' },
    'Huber Heights Wayne': { rival: 'Centerville', name: 'GWOC Rivalry' },
    'Kettering Fairmont': { rival: 'Centerville', name: 'Battle of Far Hills' },
    'Lakewood St. Edward': { rival: 'Cleveland St. Ignatius', name: 'The Holy War' },
    Lancaster: { rival: 'Newark', name: 'OCC Buckeye Rivalry' },
    'Lewis Center Olentangy': { rival: 'Powell Olentangy Liberty', name: 'Olentangy District Rivalry' },
    'Lewis Center Olentangy Orange': { rival: 'Powell Olentangy Liberty', name: 'Olentangy District Rivalry' },
    'Liberty Township Lakota East': { rival: 'West Chester Lakota West', name: 'Lakota Showdown' },
    Marysville: { rival: 'Dublin Jerome', name: 'OCC Cardinal Rivalry' },
    Medina: { rival: 'Brunswick', name: 'Medina County Rivalry' },
    Middletown: { rival: 'Hamilton', name: 'Butler County Rivalry' },
    Newark: { rival: 'Lancaster', name: 'Route 37 Rivalry' },
    'Pickerington Central': { rival: 'Pickerington North', name: 'Battle of Pickerington' },
    'Pickerington North': { rival: 'Pickerington Central', name: 'Battle of Pickerington' },
    'Powell Olentangy Liberty': { rival: 'Lewis Center Olentangy Orange', name: 'Olentangy District Rivalry' },
    Reynoldsburg: { rival: 'Gahanna Lincoln', name: 'East Columbus Rivalry' },
    Springfield: { rival: 'Centerville', name: 'GWOC Rivalry' },
    Strongsville: { rival: 'Brunswick', name: 'GCC Rivalry' },
    'Upper Arlington': { rival: 'Dublin Coffman', name: 'OCC Central Rivalry' },
    'West Chester Lakota West': { rival: 'Liberty Township Lakota East', name: 'Lakota Showdown' },
    'Westerville Central': { rival: 'Westerville North', name: 'Westerville City Rivalry' },
    'Westerville North': { rival: 'Westerville South', name: 'Westerville Rivalry' },
    'Westerville South': { rival: 'Westerville North', name: 'Westerville Rivalry' }
  },
  Pennsylvania: {
    Abington: { rival: 'Cheltenham', name: 'Oldest Montgomery County Rivalry' },
    'Abraham Lincoln': { rival: 'Father Judge', name: 'Thanksgiving Day Rivalry' },
    'Allentown Dieruff': { rival: 'Allentown William Allen', name: 'Allentown City Rivalry' },
    'Allentown William Allen': { rival: 'Allentown Dieruff', name: 'Allentown City Rivalry' },
    Bensalem: { rival: 'Harry S. Truman', name: 'Lower Bucks Rivalry' },
    'Bethlehem Freedom': { rival: 'Bethlehem Liberty', name: 'Bethlehem City Rivalry' },
    'Bethlehem Liberty': { rival: 'Bethlehem Freedom', name: 'Bethlehem City Rivalry' },
    Carlisle: { rival: 'Cumberland Valley', name: 'Cumberland County Rivalry' },
    'Central Bucks East': { rival: 'Central Bucks West', name: 'Doylestown Rivalry' },
    'Central Bucks South': { rival: 'Central Bucks West', name: 'Central Bucks Rivalry' },
    'Central Bucks West': { rival: 'Central Bucks East', name: 'Doylestown Rivalry' },
    'Central Dauphin': { rival: 'Central Dauphin East', name: 'Ram-Panther Bowl' },
    'Central Dauphin East': { rival: 'Central Dauphin', name: 'Ram-Panther Bowl' },
    'Central High': { rival: 'Northeast High', name: 'Wooden Horse Rivalry' },
    Chambersburg: { rival: 'Carlisle', name: 'I-81 Rivalry' },
    Cheltenham: { rival: 'Abington', name: 'Oldest Montgomery County Rivalry' },
    Coatesville: { rival: 'Downingtown West', name: 'Chest-Mont Rivalry' },
    'Cumberland Valley': { rival: 'Central Dauphin', name: 'Mid-Penn Commonwealth Rivalry' },
    'Downingtown East': { rival: 'Downingtown West', name: 'Battle of the Brandywine' },
    'Downingtown West': { rival: 'Downingtown East', name: 'Battle of the Brandywine' },
    'East Stroudsburg South': { rival: 'Stroudsburg', name: 'Little Brown Jug' },
    'Easton Area': { rival: 'Phillipsburg', name: 'Thanksgiving Day Game', rivalState: 'New Jersey' },
    Emmaus: { rival: 'Parkland', name: 'Battle of the Cedar Crest' },
    'Erie High': { rival: 'Erie McDowell', name: 'Erie County Rivalry' },
    'Father Judge': { rival: 'Abraham Lincoln', name: 'Thanksgiving Day Rivalry' },
    Frankford: { rival: 'Cheltenham', name: 'Public League Rivalry' },
    'Harry S. Truman': { rival: 'Bensalem', name: 'Lower Bucks Rivalry' },
    'Hazleton Area': { rival: 'Wilkes-Barre Area', name: 'Wyoming Valley Conference Rivalry' },
    'Hempfield (D3)': { rival: 'Manheim Township', name: 'Lancaster County Rivalry' },
    'La Salle College High': { rival: 'St. Joseph\'s Prep', name: 'Philadelphia Catholic League Rivalry' },
    'Manheim Township': { rival: 'Hempfield (D3)', name: 'Lancaster County Rivalry' },
    'Nazareth Area': { rival: 'Easton Area', name: 'Northampton County Rivalry' },
    Neshaminy: { rival: 'Pennsbury', name: 'Lower Bucks Rivalry' },
    'North Penn': { rival: 'Souderton', name: 'Lansdale Area Rivalry' },
    'Northeast High': { rival: 'Central High', name: 'Wooden Horse Rivalry (Oldest Public)' },
    Norwin: { rival: 'Hempfield Area', name: 'Westmoreland County Rivalry' },
    'Owen J. Roberts': { rival: 'Spring-Ford', name: 'Pioneer Athletic Conference Rivalry' },
    Parkland: { rival: 'Emmaus', name: 'Battle of the Cedar Crest' },
    'Penn Manor': { rival: 'Hempfield (D3)', name: 'Lancaster-Lebanon Section 1 Rivalry' },
    Pennridge: { rival: 'Quakertown', name: 'Thanksgiving Day Rivalry' },
    Pennsbury: { rival: 'Neshaminy', name: 'Lower Bucks Rivalry' },
    'Perkiomen Valley': { rival: 'Spring-Ford', name: 'PAC Liberty Rivalry' },
    'Pittsburgh Central Catholic': { rival: 'North Allegheny', name: 'WPIAL Class 6A Rivalry' },
    Quakertown: { rival: 'Pennridge', name: 'Thanksgiving Day Rivalry' },
    'Seneca Valley': { rival: 'North Allegheny', name: 'I-79 Rivalry' },
    Souderton: { rival: 'North Penn', name: 'Suburban One Rivalry' },
    'Spring-Ford': { rival: 'Perkiomen Valley', name: 'PAC Liberty Rivalry' },
    'St. Joseph\'s Prep': { rival: 'La Salle College High', name: 'Philadelphia Catholic League Rivalry' },
    'State College Area': { rival: 'Altoona Area', name: 'I-99 Rivalry' },
    Stroudsburg: { rival: 'East Stroudsburg South', name: 'Little Brown Jug' },
    'Wilkes-Barre Area': { rival: 'Hazleton Area', name: 'Luzerne County Rivalry' }
  },
  Tennessee: {
    Alcoa: { rival: 'Maryville', name: 'Battle of Pistol Creek' },
    Antioch: { rival: 'Cane Ridge', name: 'Southeast Nashville Rivalry' },
    Arlington: { rival: 'Bartlett', name: 'Highway 70 Rivalry' },
    Bartlett: { rival: 'Arlington', name: 'Northeast Shelby Rivalry' },
    Bearden: { rival: 'Farragut', name: 'Battle of Kingston Pike' },
    Blackman: { rival: 'Riverdale', name: 'Murfreesboro Rivalry' },
    'Bradley Central': { rival: 'Cleveland High', name: 'Battle of Cleveland' },
    'Brentwood High': { rival: 'Ravenwood', name: 'Battle of the Woods' },
    'Cane Ridge': { rival: 'Antioch', name: 'Southeast Nashville Rivalry' },
    Centennial: { rival: 'Franklin High', name: 'Battle of Franklin' },
    'Clarksville High': { rival: 'Rossview', name: 'Battle for the Warfield Shield' },
    'Cleveland High': { rival: 'Bradley Central', name: 'Battle of Cleveland' },
    Collierville: { rival: 'Germantown High', name: 'East Shelby County Rivalry' },
    Cordova: { rival: 'Bartlett', name: 'Wolfchase Rivalry' },
    'Dobyns-Bennett': { rival: 'Science Hill', name: 'Tri-Cities Rivalry' },
    Farragut: { rival: 'Bearden', name: 'Battle of Kingston Pike' },
    'Franklin High': { rival: 'Centennial', name: 'Battle of Franklin' },
    Gallatin: { rival: 'Hendersonville', name: 'Sumner County Rivalry' },
    'Germantown High': { rival: 'Houston', name: 'Germantown City Rivalry' },
    Glencliff: { rival: 'Antioch', name: 'South Nashville Rivalry' },
    'Green Hill': { rival: 'Mt. Juliet', name: 'Battle of the Golden Bear' },
    'Hardin Valley Academy': { rival: 'Farragut', name: 'West Knox Rivalry' },
    Hillsboro: { rival: 'John Overton', name: 'South Nashville Rivalry' },
    Houston: { rival: 'Collierville', name: 'Suburban Memphis Rivalry' },
    Independence: { rival: 'Summit', name: 'Spring Hill / Thompson\'s Station Rivalry' },
    'John Overton': { rival: 'Hillsboro', name: 'South Nashville Rivalry' },
    Kirkwood: { rival: 'Rossview', name: 'Exit 8 Rivalry' },
    LaVergne: { rival: 'Smyrna High', name: 'North Rutherford Rivalry' },
    Lebanon: { rival: 'Mt. Juliet', name: 'Wilson County Rivalry' },
    Maryville: { rival: 'Alcoa', name: 'Battle of Pistol Creek' },
    McGavock: { rival: 'Hunters Lane', name: 'North/East Nashville Rivalry' },
    'Mt. Juliet': { rival: 'Green Hill', name: 'Mt. Juliet City Rivalry' },
    Oakland: { rival: 'Riverdale', name: 'Battle of the \'Boro' },
    Ravenwood: { rival: 'Brentwood High', name: 'Battle of the Woods' },
    Riverdale: { rival: 'Oakland', name: 'Battle of the \'Boro' },
    Rockvale: { rival: 'Stewarts Creek', name: 'Rutherford County Rivalry' },
    Rossview: { rival: 'Clarksville High', name: 'Battle for the Warfield Shield' },
    'Science Hill': { rival: 'Dobyns-Bennett', name: 'Tri-Cities Rivalry' },
    Siegel: { rival: 'Oakland', name: 'North Murfreesboro Rivalry' },
    'Smyrna High': { rival: 'LaVergne', name: 'North Rutherford Rivalry' },
    'Stewarts Creek': { rival: 'Smyrna High', name: 'Smyrna Area Rivalry' },
    Summit: { rival: 'Independence', name: 'Spring Hill Rivalry' },
    'West Creek': { rival: 'Kenwood', name: 'North Clarksville Rivalry' },
    'West Ridge': { rival: 'Dobyns-Bennett', name: 'Sullivan County Rivalry' },
    'Wilson Central': { rival: 'Lebanon', name: 'Wilson County Rivalry' }
  },
  Texas: {
    'Alief Elsik': { rival: 'Alief Hastings', name: 'Crump Stadium Rivalry' },
    'Alief Hastings': { rival: 'Alief Elsik', name: 'Crump Stadium Rivalry' },
    'Alief Taylor': { rival: 'Alief Elsik', name: 'Alief ISD Rivalry' },
    Allen: { rival: 'Plano East', name: 'East Collin County Rivalry' },
    'Alvin High': { rival: 'Manvel', name: 'Original Alvin ISD Rivalry' },
    'Alvin Shadow Creek': { rival: 'Pearland', name: 'Highway 288 Rivalry' },
    'Arlington Bowie': { rival: 'Arlington Martin', name: 'Southwest Arlington Rivalry' },
    'Arlington High': { rival: 'Arlington Sam Houston', name: 'Original Arlington Rivalry' },
    'Arlington Lamar': { rival: 'Arlington High', name: 'North/Central Arlington Rivalry' },
    'Arlington Martin': { rival: 'Arlington Bowie', name: 'Southwest Arlington Rivalry' },
    'Arlington Sam Houston': { rival: 'Arlington High', name: 'Original Arlington Rivalry' },
    'Austin Bowie': { rival: 'Austin High', name: 'South Austin Rivalry' },
    'Austin High': { rival: 'Austin Bowie', name: 'Austin ISD Rivalry' },
    'Austin Lake Travis': { rival: 'Austin Westlake', name: 'Battle of the Lakes' },
    'Austin Vandegrift': { rival: 'Cedar Park Vista Ridge', name: 'Leander ISD 6A Rivalry' },
    'Austin Westlake': { rival: 'Austin Lake Travis', name: 'Battle of the Lakes' },
    Bridgeland: { rival: 'Cypress Ranch', name: 'Fry Road Rivalry' },
    'Buda Johnson': { rival: 'Buda Hays', name: 'Battle of Buda' },
    'Cedar Hill': { rival: 'DeSoto', name: 'Battle of Belt Line' },
    'Cedar Park High': { rival: 'Leander Rouse', name: 'Leander ISD Rivalry' },
    'Cedar Park Vista Ridge': { rival: 'Cedar Park High', name: 'Cedar Park City Rivalry' },
    'Cibolo Steele': { rival: 'Schertz Clemens', name: 'Battle of 3009' },
    'Conroe Caney Creek': { rival: 'Willis', name: 'East Montgomery County Rivalry' },
    'Conroe Grand Oaks': { rival: 'Conroe Oak Ridge', name: 'South Montgomery County Rivalry' },
    'Conroe High': { rival: 'The Woodlands', name: 'Conroe ISD Rivalry' },
    'Conroe Oak Ridge': { rival: 'Conroe Grand Oaks', name: 'South Montgomery County Rivalry' },
    'Converse Judson': { rival: 'Cibolo Steele', name: 'I-35 Corridor Rivalry' },
    Coppell: { rival: 'Southlake Carroll', name: 'DFW Suburban Rivalry' },
    'Copperas Cove': { rival: 'Killeen High', name: 'Highway 190 Rivalry' },
    Crowley: { rival: 'North Crowley', name: 'Crowley ISD Rivalry' },
    'Cy-Fair': { rival: 'Jersey Village', name: 'Original CFISD Rivalry' },
    'Cypress Creek': { rival: 'Jersey Village', name: 'CFISD Rivalry' },
    'Cypress Falls': { rival: 'Cypress Creek', name: 'CFISD Rivalry' },
    'Cypress Lakes': { rival: 'Cypress Springs', name: 'South CFISD Rivalry' },
    'Cypress Park': { rival: 'Cypress Lakes', name: 'South CFISD Rivalry' },
    'Cypress Ranch': { rival: 'Bridgeland', name: 'Fry Road Rivalry' },
    'Cypress Ridge': { rival: 'Cypress Falls', name: 'CFISD Rivalry' },
    'Cypress Springs': { rival: 'Langham Creek', name: 'FM 529 Rivalry' },
    'Cypress Woods': { rival: 'Cy-Fair', name: '290 Rivalry' },
    'Del Rio': { rival: 'Eagle Pass', name: 'Border Battle' },
    'Del Valle': { rival: 'Manor', name: 'East Travis County Rivalry' },
    'Denton Braswell': { rival: 'Little Elm', name: 'Battle of Highway 380' },
    DeSoto: { rival: 'Cedar Hill', name: 'Battle of Belt Line' },
    Dickinson: { rival: 'League City Clear Springs', name: 'I-45 Rivalry' },
    'Dripping Springs': { rival: 'Buda Johnson', name: 'Hays County Rivalry' },
    Duncanville: { rival: 'DeSoto', name: 'Battle of Belt Line' },
    'Eagle Pass': { rival: 'Del Rio', name: 'Border Battle' },
    'Edinburg Economedes': { rival: 'Edinburg North', name: 'Edinburg CISD Rivalry' },
    'Edinburg North': { rival: 'Edinburg High', name: 'Battle of Edinburg' },
    'El Paso Coronado': { rival: 'El Paso Franklin', name: 'Westside Bowl' },
    'El Paso Eastlake': { rival: 'El Paso Pebble Hills', name: 'SISD Rivalry' },
    'El Paso Franklin': { rival: 'El Paso Coronado', name: 'Westside Bowl' },
    'El Paso Montwood': { rival: 'El Paso Socorro', name: 'SISD Rivalry' },
    'El Paso Pebble Hills': { rival: 'El Paso Eastlake', name: 'SISD Rivalry' },
    'Euless Trinity': { rival: 'Hurst L.D. Bell', name: 'HEB Bowl' },
    Forney: { rival: 'North Forney', name: 'City Bank Cup' },
    'Fort Bend Austin': { rival: 'Fort Bend Clements', name: 'Sugar Land Rivalry' },
    'Fort Bend Clements': { rival: 'Fort Bend Dulles', name: 'Sugar Land Rivalry' },
    'Fort Bend Dulles': { rival: 'Fort Bend Clements', name: 'Original FBISD Rivalry' },
    'Fort Bend Elkins': { rival: 'Fort Bend Clements', name: 'Southeast FBISD Rivalry' },
    'Fort Bend Hightower': { rival: 'Fort Bend Elkins', name: 'Missouri City Rivalry' },
    'Fort Bend Ridge Point': { rival: 'Fort Bend Hightower', name: 'Missouri City Rivalry' },
    'Fort Bend Travis': { rival: 'Fort Bend Austin', name: 'Pecan Grove/New Territory Rivalry' },
    'Friendswood Clear Brook': { rival: 'Houston Clear Lake', name: 'North CCISD Rivalry' },
    Fulshear: { rival: 'Richmond Foster', name: 'LCISD West Rivalry' },
    'Galena Park North Shore': { rival: 'Humble Atascocita', name: 'District 21-6A Rivalry' },
    'Garland High': { rival: 'South Garland', name: 'Original Garland Rivalry' },
    'Garland Lakeview Centennial': { rival: 'Garland Rowlett', name: 'East Garland ISD Rivalry' },
    'Garland Naaman Forest': { rival: 'Garland Sachse', name: 'North Garland ISD Rivalry' },
    'Garland Rowlett': { rival: 'Garland Sachse', name: 'Garland ISD Rivalry' },
    'Garland Sachse': { rival: 'Garland Rowlett', name: 'Northeast Garland ISD Rivalry' },
    'Grand Prairie': { rival: 'South Grand Prairie', name: 'Gopher-Warrior Bowl' },
    'Harlingen High': { rival: 'San Benito', name: 'Battle of the Arroyo' },
    'Harlingen South': { rival: 'Harlingen High', name: 'Bird Bowl' },
    'Houston Aldine': { rival: 'Houston MacArthur', name: 'Original Aldine Rivalry' },
    'Houston Bellaire': { rival: 'Houston Lamar', name: 'Battle of the Loop' },
    'Houston Benjamin Davis': { rival: 'Houston Nimitz', name: 'Aldine ISD Rivalry' },
    'Houston C.E. King': { rival: 'Galena Park North Shore', name: 'East Beltway Rivalry' },
    'Houston Clear Lake': { rival: 'Friendswood Clear Brook', name: 'North CCISD Rivalry' },
    'Houston Eisenhower': { rival: 'Houston Aldine', name: 'Aldine ISD Rivalry' },
    'Houston Heights': { rival: 'Houston Lamar', name: 'HISD 6A Rivalry' },
    'Houston Lamar': { rival: 'Houston Bellaire', name: 'Battle of the Loop' },
    'Houston MacArthur': { rival: 'Houston Aldine', name: 'Original Aldine Rivalry' },
    'Houston Memorial': { rival: 'Houston Stratford', name: 'The Tully Bowl' },
    'Houston Nimitz': { rival: 'Houston MacArthur', name: 'Aldine ISD Rivalry' },
    'Houston Northbrook': { rival: 'Houston Spring Woods', name: 'North Spring Branch Rivalry' },
    'Houston Spring Woods': { rival: 'Houston Northbrook', name: 'North Spring Branch Rivalry' },
    'Houston Stratford': { rival: 'Houston Memorial', name: 'The Tully Bowl' },
    'Houston Westside': { rival: 'Houston Westbury', name: 'West Houston HISD Rivalry' },
    'Humble Atascocita': { rival: 'Humble Kingwood', name: 'Lake Houston Rivalry' },
    'Humble High': { rival: 'Humble Kingwood', name: 'Battle of the San Jacinto' },
    'Humble Kingwood': { rival: 'Humble High', name: 'Battle of the San Jacinto' },
    'Humble Summer Creek': { rival: 'Humble Atascocita', name: 'Beltway 8 / Humble ISD Rivalry' },
    'Hurst L.D. Bell': { rival: 'Euless Trinity', name: 'HEB Bowl' },
    'Irving High': { rival: 'Irving Nimitz', name: 'Irving ISD Rivalry' },
    'Irving MacArthur': { rival: 'Irving Nimitz', name: 'Irving ISD Rivalry' },
    'Irving Nimitz': { rival: 'Irving High', name: 'Irving ISD Rivalry' },
    'Jersey Village': { rival: 'Cypress Creek', name: 'Original CFISD Rivalry' },
    'Justin Northwest': { rival: 'Northwest Eaton', name: 'Northwest ISD Rivalry' },
    'Katy Cinco Ranch': { rival: 'Katy Seven Lakes', name: 'South Katy Rivalry' },
    'Katy High': { rival: 'Katy Cinco Ranch', name: 'Katy ISD Rivalry' },
    'Katy Jordan': { rival: 'Katy Tompkins', name: 'Southwest Katy Rivalry' },
    'Katy Mayde Creek': { rival: 'Katy Morton Ranch', name: 'Northeast Katy Rivalry' },
    'Katy Morton Ranch': { rival: 'Katy Mayde Creek', name: 'Northeast Katy Rivalry' },
    'Katy Paetow': { rival: 'Katy Morton Ranch', name: 'North Katy Rivalry' },
    'Katy Seven Lakes': { rival: 'Katy Cinco Ranch', name: 'South Katy Rivalry' },
    'Katy Taylor': { rival: 'Katy High', name: 'Original Katy Rivalry' },
    'Katy Tompkins': { rival: 'Katy Seven Lakes', name: 'South Katy Rivalry' },
    Keller: { rival: 'Keller Central', name: 'Keller Cup' },
    'Keller Central': { rival: 'Keller', name: 'Keller Cup' },
    'Keller Timber Creek': { rival: 'Keller Central', name: 'Keller ISD Rivalry' },
    'Killeen Harker Heights': { rival: 'Killeen Shoemaker', name: 'Killeen ISD Rivalry' },
    'Klein Cain': { rival: 'Klein High', name: 'Klein ISD Rivalry' },
    'Klein Collins': { rival: 'Klein Oak', name: 'North Klein Rivalry' },
    'Klein Forest': { rival: 'Klein High', name: 'Original Klein Rivalry' },
    'Klein High': { rival: 'Klein Forest', name: 'Original Klein Rivalry' },
    'Klein Oak': { rival: 'Klein High', name: 'Original Klein Rivalry' },
    Lancaster: { rival: 'Cedar Hill', name: 'I-35E / Belt Line Rivalry' },
    'Langham Creek': { rival: 'Cypress Falls', name: 'Copperfield Rivalry' },
    'Laredo Alexander': { rival: 'Laredo United', name: 'North Laredo Rivalry' },
    'Laredo LBJ': { rival: 'Laredo United South', name: 'South Laredo Rivalry' },
    'Laredo United': { rival: 'Laredo Alexander', name: 'North Laredo Rivalry' },
    'Laredo United South': { rival: 'Laredo LBJ', name: 'South Laredo Rivalry' },
    'League City Clear Creek': { rival: 'Houston Clear Lake', name: 'Original CCISD Rivalry' },
    'League City Clear Falls': { rival: 'League City Clear Springs', name: 'CCISD Rivalry' },
    'League City Clear Springs': { rival: 'League City Clear Creek', name: 'League City Rivalry' },
    'Leander High': { rival: 'Cedar Park High', name: 'Battle for the Bell' },
    'Leander Rouse': { rival: 'Leander High', name: 'Battle of Raider Way' },
    Lewisville: { rival: 'Lewisville Marcus', name: 'Battle of the Axe' },
    'Lewisville Flower Mound': { rival: 'Lewisville Marcus', name: 'Mound Showdown' },
    'Lewisville Hebron': { rival: 'Plano West', name: 'District 6-6A Rivalry' },
    'Lewisville Marcus': { rival: 'Lewisville Flower Mound', name: 'Mound Showdown' },
    'Magnolia High': { rival: 'Magnolia West', name: 'Magnolia Bowl' },
    'Magnolia West': { rival: 'Magnolia High', name: 'Magnolia Bowl' },
    Manor: { rival: 'Del Valle', name: 'East Travis County Rivalry' },
    'Mansfield Lake Ridge': { rival: 'Mansfield Legacy', name: 'Mansfield ISD Rivalry' },
    'Mansfield Legacy': { rival: 'Mansfield', name: 'Mansfield ISD Rivalry' },
    Manvel: { rival: 'Alvin Shadow Creek', name: 'Alvin ISD Rivalry' },
    McKinney: { rival: 'McKinney Boyd', name: 'Crosstown Showdown' },
    'McKinney Boyd': { rival: 'McKinney', name: 'Crosstown Showdown' },
    'Mesquite Horn': { rival: 'Mesquite High', name: 'Mesquite ISD Rivalry' },
    'Midland High': { rival: 'Midland Legacy', name: 'Tall City Rivalry' },
    'Midland Legacy': { rival: 'Midland High', name: 'Tall City Rivalry' },
    'North Crowley': { rival: 'Crowley', name: 'Crowley ISD Rivalry' },
    'North Forney': { rival: 'Forney', name: 'City Bank Cup' },
    'North Garland': { rival: 'Garland High', name: 'Garland ISD Rivalry' },
    'Northwest Eaton': { rival: 'Justin Northwest', name: 'Northwest ISD Rivalry' },
    'Northwest Nelson (Byron)': { rival: 'Southlake Carroll', name: 'Trophy Club/Southlake Rivalry' },
    'Odessa High': { rival: 'Odessa Permian', name: 'Crosstown Showdown' },
    'Odessa Permian': { rival: 'Midland Legacy', name: 'Permian Basin Rivalry' },
    'Pasadena Dobie': { rival: 'Pasadena Memorial', name: 'South Belt Rivalry' },
    'Pasadena Memorial': { rival: 'Pasadena Dobie', name: 'Pasadena ISD Rivalry' },
    'Pasadena South Houston': { rival: 'Pasadena Rayburn', name: 'Pasadena ISD Rivalry' },
    Pearland: { rival: 'Pearland Dawson', name: 'Pearland Bowl' },
    'Pearland Dawson': { rival: 'Pearland', name: 'Pearland Bowl' },
    'Plano East': { rival: 'Plano Senior', name: 'East-West Bowl' },
    'Plano Senior': { rival: 'Plano East', name: 'East-West Bowl' },
    'Plano West': { rival: 'Plano Senior', name: 'Plano ISD Rivalry' },
    Prosper: { rival: 'Prosper Rock Hill', name: 'Prosper ISD Rivalry' },
    'Prosper Rock Hill': { rival: 'Prosper', name: 'Prosper ISD Rivalry' },
    'Richardson Berkner': { rival: 'Richardson Lake Highlands', name: 'RISD Rivalry' },
    'Richardson High': { rival: 'Richardson Berkner', name: 'RISD Rivalry' },
    'Richardson Lake Highlands': { rival: 'Richardson Pearce', name: 'RISD Rivalry' },
    'Richardson Pearce': { rival: 'Richardson High', name: 'Battle of Belt Line' },
    'Richmond George Ranch': { rival: 'Richmond Foster', name: 'LCISD Rivalry' },
    Rockwall: { rival: 'Rockwall-Heath', name: 'I-30 Classic' },
    'Rockwall-Heath': { rival: 'Rockwall', name: 'I-30 Classic' },
    'Round Rock Cedar Ridge': { rival: 'Round Rock Stony Point', name: 'East Round Rock Rivalry' },
    'Round Rock High': { rival: 'Round Rock Stony Point', name: 'Round Rock City Rivalry' },
    'Round Rock McNeil': { rival: 'Round Rock Westwood', name: 'South RRISD Rivalry' },
    'Round Rock Stony Point': { rival: 'Round Rock High', name: 'Round Rock City Rivalry' },
    'Round Rock Westwood': { rival: 'Round Rock McNeil', name: 'South RRISD Rivalry' },
    'Royse City': { rival: 'Rockwall-Heath', name: 'District 10-6A Rivalry' },
    'San Antonio Brandeis': { rival: 'San Antonio O\'Connor', name: 'Northwest NISD Rivalry' },
    'San Antonio Brennan': { rival: 'San Antonio Harlan', name: 'Far West NISD Rivalry' },
    'San Antonio Churchill': { rival: 'San Antonio Clark', name: 'Gucci Bowl' },
    'San Antonio Clark': { rival: 'San Antonio Churchill', name: 'Gucci Bowl' },
    'San Antonio East Central': { rival: 'Converse Judson', name: 'East Bexar County Rivalry' },
    'San Antonio Harlan': { rival: 'San Antonio Taft', name: 'Culebra Road Rivalry' },
    'San Antonio Holmes': { rival: 'San Antonio Jay', name: 'Original NISD Rivalry' },
    'San Antonio Jay': { rival: 'San Antonio Holmes', name: 'Original NISD Rivalry' },
    'San Antonio Johnson': { rival: 'San Antonio Reagan', name: 'Battle of 281' },
    'San Antonio Madison': { rival: 'San Antonio Reagan', name: 'NEISD Rivalry' },
    'San Antonio Marshall': { rival: 'San Antonio Holmes', name: 'Original NISD Rivalry' },
    'San Antonio O\'Connor': { rival: 'San Antonio Brandeis', name: 'Northwest NISD Rivalry' },
    'San Antonio Reagan': { rival: 'San Antonio Johnson', name: 'Battle of 281' },
    'San Antonio Roosevelt': { rival: 'San Antonio Madison', name: 'Northeast NEISD Rivalry' },
    'San Antonio Sotomayor': { rival: 'San Antonio Harlan', name: 'NISD Rivalry' },
    'San Antonio Stevens': { rival: 'San Antonio Warren', name: 'Potranco Road Rivalry' },
    'San Antonio Taft': { rival: 'San Antonio Warren', name: 'West NISD Rivalry' },
    'San Antonio Warren': { rival: 'San Antonio Stevens', name: 'Potranco Road Rivalry' },
    'San Benito': { rival: 'Harlingen High', name: 'Battle of the Arroyo' },
    'Schertz Clemens': { rival: 'Cibolo Steele', name: 'Battle of 3009' },
    'South Garland': { rival: 'Garland High', name: 'Original Garland Rivalry' },
    'South Grand Prairie': { rival: 'Grand Prairie', name: 'Gopher-Warrior Bowl' },
    'Southlake Carroll': { rival: 'Northwest Nelson (Byron)', name: 'Highway 114 Rivalry' },
    'Spring Dekaney': { rival: 'Spring Westfield', name: 'Spring ISD Rivalry' },
    'Spring High': { rival: 'Spring Westfield', name: 'Original Spring ISD Rivalry' },
    'Spring Westfield': { rival: 'Spring Dekaney', name: 'Spring ISD Rivalry' },
    'The Woodlands': { rival: 'Woodlands College Park', name: 'War on the Woods' },
    'Tomball High': { rival: 'Tomball Memorial', name: 'The Patriotic Show' },
    'Tomball Memorial': { rival: 'Tomball High', name: 'The Patriotic Show' },
    'Waco Midway': { rival: 'Temple', name: 'I-35 Central Texas Rivalry' },
    Willis: { rival: 'Conroe High', name: 'Montgomery County Rivalry' },
    'Woodlands College Park': { rival: 'The Woodlands', name: 'War on the Woods' },
    'Wylie East': { rival: 'Wylie High', name: 'Crosstown Showdown' },
    'Wylie High': { rival: 'Wylie East', name: 'Crosstown Showdown' }
  }
};

/** A team as these helpers need it: its school name and state (Texas when unset, like the ranking engines). */
export interface RivalryTeam {
  name: string;
  state?: string;
}

const stateOf = (t: RivalryTeam) => t.state ?? 'Texas';

/** The team's own rival: the school, its state and the rivalry's name; undefined when it has none in the game. */
export function rivalOf(team: RivalryTeam): { school: string; state: string; name: string } | undefined {
  const r = RIVALRIES[stateOf(team)]?.[team.name];
  return r && { school: r.rival, state: r.rivalState ?? stateOf(team), name: r.name };
}

const lists = (a: RivalryTeam, b: RivalryTeam) => {
  const r = rivalOf(a);
  return r !== undefined && r.school === b.name && r.state === stateOf(b);
};

/** True when either team lists the other as its rival (home and away in either order). */
export function isRivalryGame(a: RivalryTeam, b: RivalryTeam): boolean {
  return lists(a, b) || lists(b, a);
}

/**
 * The game's rivalry name, or undefined when it isn't a rivalry game. The first team's name for it wins when both
 * list each other under different names, so pass the coach's team first for the coach's view.
 */
export function rivalryName(a: RivalryTeam, b: RivalryTeam): string | undefined {
  if (lists(a, b)) return rivalOf(a)!.name;
  if (lists(b, a)) return rivalOf(b)!.name;
  return undefined;
}
